import { RetrieveCardRequest } from '@square/dashboard-protos/models/squareup/bbdebit/cardmanagement';
import { GetCardBillingAddressRequest, SetCardBillingAddressRequest } from '@square/dashboard-protos/models/squareup/bizbank/service';
import { GlobalAddress } from '@square/dashboard-protos/models/squareup/common/location';
import { Country } from '@square/dashboard-protos/models/squareup/common/countries';
import workflow from '@banking/ui/lib/workflow';
import getPostalCodeFormatter from '@banking/ui/utils/field-kit-formatters/postal-code';
import * as addressValidationWorkflow from '@banking/ui/workflows/common/address-validation';
import { decrementNavigation, getNavigationWithInjectedScreen, incrementNavigation } from '@banking/ui/workflows/common/utils';
import { setViewedReplacementCardAddressPopup } from '@banking/ui/utils/replacement-card-address-popup';
export let BillAddressError = /*#__PURE__*/function (BillAddressError) {
  BillAddressError["None"] = "BILLING_ADDRESS_ERROR_NONE";
  BillAddressError["Generic"] = "BILLING_ADDRESS_ERROR_GENERIC";
  return BillAddressError;
}({});
export let Loading = /*#__PURE__*/function (Loading) {
  Loading["None"] = "BILLING_ADDRESS_LOADING_NONE";
  Loading["ModalButton"] = "BILLING_ADDRESS_LOADING_BUTTON";
  Loading["Modal"] = "BILLING_ADDRESS_LOADING_MODAL";
  Loading["InBackground"] = "BILLING_ADDRESS_LOADING_IN_BACKGROUND";
  return Loading;
}({});
export let Screen = /*#__PURE__*/function (Screen) {
  Screen["NONE"] = "BILLING_ADDRESS/NONE";
  Screen["CONFIRM_ADDRESS"] = "BILLING_ADDRESS/CONFIRM_ADDRESS";
  Screen["EDIT_ADDRESS"] = "BILLING_ADDRESS/EDIT_ADDRESS";
  Screen["EDIT_ADDRESS_FOR_REPLACEMENT_CARD"] = "BILLING_ADDRESS/EDIT_ADDRESS_FOR_REPLACEMENT_CARD";
  Screen["ADDRESS_STANDARDIZED"] = "BILLING_ADDRESS/ADDRESS_STANDARDIZED";
  Screen["ADDRESS_UNVERIFIED"] = "BILLING_ADDRESS/ADDRESS_UNVERIFIED";
  Screen["SUCCESS"] = "BILLING_ADDRESS/SUCCESS";
  return Screen;
}({});
export let InitializationReason = /*#__PURE__*/function (InitializationReason) {
  InitializationReason[InitializationReason["FETCH_ADDRESS"] = 0] = "FETCH_ADDRESS";
  InitializationReason[InitializationReason["CLICK_EDIT_ADDRESS"] = 1] = "CLICK_EDIT_ADDRESS";
  InitializationReason[InitializationReason["CLICK_WRONG_REPLACEMENT_CARD_ADDRESS"] = 2] = "CLICK_WRONG_REPLACEMENT_CARD_ADDRESS";
  InitializationReason[InitializationReason["OPEN_CONFIRM_REPLACEMENT_CARD_ADDRESS_POPUP"] = 3] = "OPEN_CONFIRM_REPLACEMENT_CARD_ADDRESS_POPUP";
  return InitializationReason;
}({});
export const EDIT_BILLING_ADDRESS_FLOW = [Screen.EDIT_ADDRESS, Screen.SUCCESS];
export const EDIT_BILLING_ADDRESS_FOR_CARD_REPLACEMENT_FLOW = [Screen.EDIT_ADDRESS_FOR_REPLACEMENT_CARD, Screen.SUCCESS];
export const CONFIRM_REPLACEMENT_CARD_ADDRESS_FLOW = [Screen.CONFIRM_ADDRESS, Screen.EDIT_ADDRESS_FOR_REPLACEMENT_CARD, Screen.SUCCESS];
export const INITIAL_STATE = {
  addressValidation: addressValidationWorkflow.createState(),
  billingAddress: GlobalAddress.create(),
  countryAddressData: null,
  error: BillAddressError.None,
  expiringCard: null,
  expiringCardToken: null,
  isOpen: false,
  loading: Loading.None,
  navigation: {
    flow: EDIT_BILLING_ADDRESS_FLOW,
    current: EDIT_BILLING_ADDRESS_FLOW[0]
  },
  unitToken: '',
  updatedBillingAddress: GlobalAddress.create()
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});
export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["BillingAddressChange"] = "CARD_SETTINGS/BILLING_ADDRESS/BILLING_ADDRESS_CHANGE";
  ActionType["Initialize"] = "CARD_SETTINGS/BILLING_ADDRESS/INITIALIZE";
  ActionType["InitializeResponsesReceived"] = "CARD_SETTINGS/BILLING_ADDRESS/INITIALIZE_RESPONSES_RECEIVED";
  ActionType["InitializeResponsesRejected"] = "CARD_SETTINGS/BILLING_ADDRESS/INITIALIZE_RESPONSES_REJECTED";
  ActionType["SetBillingAddressReceived"] = "CARD_SETTINGS/BILLING_ADDRESS/SET_BILLING_ADDRESS_RECEIVED";
  ActionType["SetBillingAddressRejected"] = "CARD_SETTINGS/BILLING_ADDRESS/SET_BILLING_ADDRESS_REJECTED";
  ActionType["ToggleModal"] = "CARD_SETTINGS/BILLING_ADDRESS/TOGGLE_MODAL";
  ActionType["UserClickBack"] = "CARD_SETTINGS/BILLING_ADDRESS/USER_CLICK_BACK";
  ActionType["UserClickNext"] = "CARD_SETTINGS/BILLING_ADDRESS/USER_CLICK_NEXT";
  ActionType["UserClickSave"] = "CARD_SETTINGS/BILLING_ADDRESS/USER_CLICK_SAVE";
  return ActionType;
}({});
export const billingAddressChangeAction = address => ({
  type: ActionType.BillingAddressChange,
  address
});
export const initializeAction = ({
  unitToken,
  reason,
  expiringCardToken = null
}) => ({
  type: ActionType.Initialize,
  unitToken,
  reason,
  expiringCardToken
});
export const initializeResponsesReceivedAction = responses => ({
  type: ActionType.InitializeResponsesReceived,
  responses
});
export const initializeResponsesRejectedAction = error => ({
  type: ActionType.InitializeResponsesRejected,
  error
});
export const setBillingAddressReceivedAction = (response, chosenBillingAddress) => ({
  type: ActionType.SetBillingAddressReceived,
  response,
  chosenBillingAddress
});
export const setBillingAddressRejectedAction = error => ({
  type: ActionType.SetBillingAddressRejected,
  error
});
export const toggleModalAction = isOpen => ({
  type: ActionType.ToggleModal,
  isOpen
});
export const userClickBackAction = () => ({
  type: ActionType.UserClickBack
});
export const userClickNextAction = () => ({
  type: ActionType.UserClickNext
});
export const userClickSaveAction = () => ({
  type: ActionType.UserClickSave
});
function handleInitialize(context, state, action) {
  const {
    expiringCardToken,
    unitToken,
    reason
  } = action;
  if (reason === InitializationReason.OPEN_CONFIRM_REPLACEMENT_CARD_ADDRESS_POPUP && !expiringCardToken) {
    context.sentry.captureException('[Billing address] Must provide expiringCardToken when initializing address confirmation popup');
    return [{
      ...state,
      error: BillAddressError.Generic,
      loading: Loading.None
    }, null];
  }
  const {
    flow,
    openModal,
    promises
  } = getBillingAddressFlow(reason, context, unitToken, expiringCardToken);
  const work = () => Promise.all(promises).then(initializeResponsesReceivedAction, initializeResponsesRejectedAction);
  return [{
    ...state,
    expiringCardToken,
    isOpen: openModal,
    loading: openModal ? Loading.Modal : Loading.InBackground,
    navigation: {
      flow,
      current: flow[0] ?? Screen.NONE
    },
    unitToken
  }, work];
}
function getBillingAddressFlow(reason, context, unitToken, expiringCardToken) {
  const getBillingAddressResponse = context.getCardBillingAddress(GetCardBillingAddressRequest.create({
    unitToken
  }));
  const lookupCountryAddressDataResponse = context.lookupCountryAddressData();
  switch (reason) {
    case InitializationReason.CLICK_WRONG_REPLACEMENT_CARD_ADDRESS:
      return {
        flow: EDIT_BILLING_ADDRESS_FOR_CARD_REPLACEMENT_FLOW,
        promises: [getBillingAddressResponse, lookupCountryAddressDataResponse],
        openModal: true
      };
    case InitializationReason.OPEN_CONFIRM_REPLACEMENT_CARD_ADDRESS_POPUP:
      if (!expiringCardToken) {
        throw new Error('Expiring card must not be null in replacement card address popup flow');
      }
      return {
        flow: CONFIRM_REPLACEMENT_CARD_ADDRESS_FLOW,
        promises: [getBillingAddressResponse, lookupCountryAddressDataResponse, context.retrieveCard(RetrieveCardRequest.create({
          cardToken: expiringCardToken,
          fieldMask: {
            paths: ['id', 'fulfillment', 'last_four', 'expiration_data.replacement_card']
          }
        }))],
        openModal: true
      };
    case InitializationReason.FETCH_ADDRESS:
      return {
        flow: [],
        promises: [getBillingAddressResponse, lookupCountryAddressDataResponse],
        openModal: false
      };
    default:
      return {
        flow: EDIT_BILLING_ADDRESS_FLOW,
        promises: [getBillingAddressResponse, lookupCountryAddressDataResponse],
        openModal: true
      };
  }
}
function handleInitializeResponsesReceived(context, state, action) {
  const [getBillingAddressResponse, countryAddressData, retrieveCardResponse] = action.responses;
  const billingAddress = getBillingAddressResponse.billingAddress;
  if (billingAddress == null) {
    context.sentry.captureException('[Billing address] Missing billing address');
    return [{
      ...state,
      error: BillAddressError.Generic,
      loading: Loading.None
    }, null];
  }

  // If a seller opens the billing address modal for an expiring card for any reason,
  // we want to mark the replacement card address popup as viewed, even if they haven't
  // actually viewed the address confirmation screen. This is because they might visit
  // the card settings page directly from an email warning that their replacement card
  // will be sent to their billing address and then click the "billing address" option.
  if (state.expiringCardToken && state.isOpen) {
    setViewedReplacementCardAddressPopup(state.expiringCardToken);
  }

  // Parse the zip code comng from the backend to prevent the UI validator from flagging it as invalid,
  // if the user saves the address without changing the zip code.
  // For example, for US addresses this removes hyphens from the zip code.
  // If the backend returns a zip code with a hyphen and the user updates the address without modifying the zip code,
  // the validator will flag it as invalid. Visually, we display long US zip codes with hyphens even if the actual value,
  // does not have any hyphens.
  const formattedPostalCode = getPostalCodeFormatter(Country[countryAddressData.countryCode]).parse(billingAddress.postalCode);
  const formattedBillingAddress = GlobalAddress.create({
    ...billingAddress,
    postalCode: formattedPostalCode
  });
  return [{
    ...state,
    billingAddress: formattedBillingAddress,
    countryAddressData,
    expiringCard: retrieveCardResponse?.card ?? null,
    loading: Loading.None,
    updatedBillingAddress: formattedBillingAddress
  }, null];
}
function handleInitializeResponsesRejected(context, state, action) {
  context.sentry.captureException(`[Billing address] Failed to fetch initial data - ${action.error}`);
  return [{
    ...state,
    error: BillAddressError.Generic,
    loading: Loading.None
  }, null];
}
function handleSetBillingAddressReceived(context, state, action) {
  const {
    response,
    chosenBillingAddress
  } = action;
  if (!response.success) {
    context.sentry.captureException('[Billing address] Failed to set billing address - success false');
    return [{
      ...state,
      error: BillAddressError.Generic,
      loading: Loading.None
    }, null];
  }
  const {
    navigation
  } = state;
  return [{
    ...state,
    billingAddress: chosenBillingAddress,
    loading: Loading.None,
    navigation: incrementNavigation(navigation),
    updatedBillingAddress: chosenBillingAddress
  }, null];
}
function handleSetBillingAddressRejected(context, state, action) {
  context.sentry.captureException(`[Billing address] Failed to set billing address - ${action.error}`);
  return [{
    ...state,
    error: BillAddressError.Generic,
    loading: Loading.None
  }, null];
}
function handleToggleModal(state, action) {
  return [{
    ...state,
    isOpen: action.isOpen
  }, null];
}
function handleUserClickSave(context, state) {
  const {
    unitToken,
    updatedBillingAddress,
    addressValidation
  } = state;
  const chosenBillingAddress = addressValidation.chosenAddress === addressValidationWorkflow.ChosenAddress.Normalized ? addressValidation.normalizedAddress : updatedBillingAddress;
  const work = () => context.setCardBillingAddress(SetCardBillingAddressRequest.create({
    unitToken,
    billingAddress: chosenBillingAddress
  })).then(response => setBillingAddressReceivedAction(response, chosenBillingAddress), setBillingAddressRejectedAction);
  return [{
    ...state,
    loading: Loading.ModalButton
  }, work];
}
function handleUserClickNext(state) {
  return [{
    ...state,
    navigation: incrementNavigation(state.navigation)
  }, null];
}
function handleUserClickBack(state) {
  const {
    navigation
  } = state;
  // if at the beginning of the flow, close the modal
  if (navigation.current === navigation.flow[0]) {
    return [state, () => toggleModalAction(false)];
  }

  // Removes one-off screens added to the flow on back
  switch (navigation.current) {
    case Screen.ADDRESS_STANDARDIZED:
    case Screen.ADDRESS_UNVERIFIED:
      {
        const updatedFlow = navigation.flow.reduce((acc, screen) => {
          if (screen === navigation.current) {
            return acc;
          }
          acc.push(screen);
          return acc;
        }, []);
        return [{
          ...state,
          navigation: {
            ...decrementNavigation(navigation),
            flow: updatedFlow
          }
        }, null];
      }
  }
  return [{
    ...state,
    navigation: decrementNavigation(navigation)
  }, null];
}
function updateNavigationAfterValidationCompleted(state, addressValidationState) {
  switch (addressValidationState.result) {
    case addressValidationWorkflow.AddressValidationResult.NORMALIZED:
      return [{
        ...state,
        navigation: getNavigationWithInjectedScreen(state.navigation, Screen.ADDRESS_STANDARDIZED, [Screen.SUCCESS])
      }, null];
    case addressValidationWorkflow.AddressValidationResult.UNVERIFIED:
      return [{
        ...state,
        navigation: getNavigationWithInjectedScreen(state.navigation, Screen.ADDRESS_UNVERIFIED, [Screen.SUCCESS])
      }, null];
    default:
      return [state, () => userClickSaveAction()];
  }
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.BillingAddressChange:
      {
        const address = action.address;
        return [{
          ...state,
          updatedBillingAddress: address
        }, null];
      }
    case ActionType.Initialize:
      return handleInitialize(context, state, action);
    case ActionType.InitializeResponsesReceived:
      return handleInitializeResponsesReceived(context, state, action);
    case ActionType.InitializeResponsesRejected:
      return handleInitializeResponsesRejected(context, state, action);
    case ActionType.SetBillingAddressReceived:
      return handleSetBillingAddressReceived(context, state, action);
    case ActionType.SetBillingAddressRejected:
      return handleSetBillingAddressRejected(context, state, action);
    case ActionType.ToggleModal:
      return handleToggleModal(state, action);
    case ActionType.UserClickBack:
      return handleUserClickBack(state);
    case ActionType.UserClickSave:
      {
        return handleUserClickSave(context, state);
      }
    case ActionType.UserClickNext:
      {
        return handleUserClickNext(state);
      }
    case addressValidationWorkflow.ActionType.ChosenAddressChange:
    case addressValidationWorkflow.ActionType.NormalizedAddressChange:
    case addressValidationWorkflow.ActionType.ValidationCompleted:
    case addressValidationWorkflow.ActionType.ValidateAddress:
    case addressValidationWorkflow.ActionType.ValidateAddressReceived:
    case addressValidationWorkflow.ActionType.ValidateAddressRejected:
      {
        const [addressValidation, nextAction] = addressValidationWorkflow.update({
          sentry: context.sentry,
          validateAddress: context.validateAddress
        })(state.addressValidation, action);
        if (action.type === addressValidationWorkflow.ActionType.ValidationCompleted) {
          return updateNavigationAfterValidationCompleted(state, addressValidation);
        }
        return [{
          ...state,
          addressValidation
        }, nextAction];
      }
  }
};
const billingAddress = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default billingAddress;